import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { useForm } from 'react-hook-form'
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material'
import {
  Stack,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Button,
  Checkbox,
  ListItemText,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import { filterByValue } from 'utils/data-manipulation/filters'
import rem from 'utils/rem'
import { FILTER_OPTION_TYPES } from 'utils/constants'
import { getISOLocaleMidnightTime } from 'utils/data-manipulation/valueFormatter'
import { addToDate } from 'utils/data-manipulation/common'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { useSelector } from 'react-redux'
import { getDateFormatPatternFromLocale } from 'utils/helpers'
import dayjs from 'dayjs'

const constructResetAdditionalFilters = (additionalFilterOptions, defaultFilter) => {
  const additionalFilterKeys = additionalFilterOptions.map(filter => filter.name)

  const filteredDefaultFilters = Object.keys(defaultFilter).reduce((acc, key) => {
    if (additionalFilterKeys.find(additionalFilterKey => additionalFilterKey === key)) {
      acc[key] = defaultFilter[key]
    }
    return acc
  }, {})

  return filteredDefaultFilters
}

export const useSearchBar = ({
  textSearchPlaceholder,
  rows,
  showFilterChips,
  filterChips,
  renderViewModeToggle,
  renderDateRangePicker,
  renderSelectFilter,
  useBackendSortAndFilter,
  filter,
  defaultFilter = { search: '' },
  setFilter,
  additionalFilterOptions = [],
  hideClearButton,
  searchBarSize = 'medium',
}) => {
  const { control, reset, watch } = useForm()

  const defaultAdditionalFilters = useMemo(() => {
    const defaultAdditionalFilterObject = {}
    additionalFilterOptions.forEach(option => {
      if (option.defaultValue) {
        defaultAdditionalFilterObject[option.name] = option.defaultValue
      } else if (option.defaultFromValue) {
        defaultAdditionalFilterObject[option.fromDateName] = option.defaultFromValue
      } else if (option.defaultToValue) {
        defaultAdditionalFilterObject[option.toDateName] = option.defaultToValue
      }
    })
    return defaultAdditionalFilterObject
  }, [])

  const [filteredRows, setFilteredRows] = useState([])
  const [textSearchValue, setTextSearchValue] = useState(filter?.search || '')
  const [additionalFilters, setAdditionalFilters] = useState(defaultAdditionalFilters)

  const { locale } = useSelector(state => state.auth)
  const { dateFormat, muiDateFormat } = getDateFormatPatternFromLocale(locale)

  const filterRows = (searchValue, theRows, isReset) => {
    if (useBackendSortAndFilter) {
      if (isReset) {
        setFilter(defaultFilter)
        return
      }
      const newFilter = {
        ...filter,
        search: searchValue,
      }
      setFilter(newFilter)
    } else {
      const newRows = filterByValue([...theRows], searchValue)
      setFilteredRows(newRows)
    }
  }

  const debounceFilterRows = useCallback(debounce(filterRows, 300), [rows])

  const handleSearchUpdate = e => {
    const { value: searchValue } = e.target
    setTextSearchValue(searchValue)
    debounceFilterRows(searchValue, rows)
  }

  useEffect(() => {
    setFilteredRows(rows)
  }, [rows])

  useEffect(() => {
    if (useBackendSortAndFilter && Object.keys(additionalFilters).length > 0) {
      const newFilter = { ...filter }
      Object.entries(additionalFilters).forEach(([key, value]) => {
        newFilter[key] = value
      })
      setFilter(newFilter)
    }
  }, [additionalFilters])

  const renderFilterOption = filterOption => {
    switch (filterOption?.type) {
      case FILTER_OPTION_TYPES.CUSTOMER_AUTOCOMPLETE:
        return (
          <CustomerAutocomplete
            key={`filter-option-key-${filterOption?.name}`}
            name={filterOption?.name}
            label={filterOption?.label}
            variant="outlined"
            control={control}
            fullWidth
            freeSolo={false}
            stackProps={{ width: filterOption?.width }}
            baseFilter={filterOption?.baseFilter}
            formControlProps={{ sx: { width: filterOption?.width } }}
            autocompleteProps={{
              renderOption: (props, option) => (
                <li {...props} key={option?.id}>
                  {option?.displayName}
                </li>
              ),
              isOptionEqualToValue: (option, value) => option?.id === value?.id,
            }}
            onChange={customer => {
              if (customer) {
                setAdditionalFilters(prevAdditionalFilters => ({
                  ...prevAdditionalFilters,
                  [filterOption?.name]: customer?.id,
                }))
              } else {
                setAdditionalFilters(prevAdditionalFilters => ({
                  ...prevAdditionalFilters,
                  [filterOption?.name]: null,
                }))
              }
            }}
          />
        )
      case FILTER_OPTION_TYPES.SELECT:
        return (
          <FormControl
            key={`filter-option-key-${filterOption?.name}`}
            sx={{ width: filterOption?.width }}
            data-testid={`${filterOption?.name}-select`}
          >
            <InputLabel id={`${filterOption?.name}-label`}>{filterOption?.label}</InputLabel>
            <Select
              label={filterOption?.label}
              labelId={`${filterOption?.name}-label`}
              value={additionalFilters[filterOption?.name] || ''}
              onChange={event => {
                const { value } = event.target
                setAdditionalFilters(prevAdditionalFilters => ({
                  ...prevAdditionalFilters,
                  [filterOption?.name]: value,
                }))
              }}
              {...(additionalFilters[filterOption?.name] && {
                IconComponent: () => null,
                endAdornment: (
                  <IconButton
                    onClick={() => {
                      setAdditionalFilters(prevAdditionalFilters => ({
                        ...prevAdditionalFilters,
                        [filterOption?.name]: null,
                      }))
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              })}
            >
              {filterOption?.options?.map(option => (
                <MenuItem key={option?.id} value={option?.id}>
                  {option?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      case FILTER_OPTION_TYPES.MULTI_SELECT: {
        const isAllSelected =
          filterOption?.options?.length > 0 &&
          additionalFilters[filterOption?.name]?.length === filterOption?.options?.length
        return (
          <FormControl
            key={`filter-option-key-${filterOption?.name}`}
            sx={{ width: filterOption?.width }}
            data-testid={`${filterOption?.name}-select`}
          >
            <InputLabel id={`${filterOption?.name}-label`}>{filterOption?.label}</InputLabel>
            <Select
              multiple
              label={filterOption?.label}
              labelId={`${filterOption?.name}-label`}
              value={additionalFilters[filterOption?.name] || []}
              onChange={event => {
                const { value } = event.target
                let selectedValues = value
                if (value[value.length - 1] === 'ALL') {
                  selectedValues = isAllSelected ? [] : filterOption?.options?.map(option => option.id) || []
                } else if (value.length === 0) {
                  selectedValues = null
                }
                if (filterOption.onChange) {
                  filterOption.onChange(selectedValues)
                }
                setAdditionalFilters(prevAdditionalFilters => ({
                  ...prevAdditionalFilters,
                  [filterOption?.name]: selectedValues,
                }))
              }}
              renderValue={selected =>
                selected.length === 1
                  ? filterOption?.options?.find(option => option.id === selected[0])?.name
                  : `${selected.length} Selected`
              }
              {...(additionalFilters[filterOption?.name] && {
                IconComponent: () => null,
                endAdornment: (
                  <IconButton
                    onClick={() => {
                      if (filterOption.onChange) {
                        filterOption.onChange(null)
                      }
                      setAdditionalFilters(prevAdditionalFilters => ({
                        ...prevAdditionalFilters,
                        [filterOption?.name]: null,
                      }))
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              })}
            >
              <MenuItem value="ALL">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={
                    additionalFilters[filterOption?.name]?.length > 0 &&
                    additionalFilters[filterOption?.name]?.length < filterOption?.options?.length
                  }
                />
                <ListItemText primary="Select All" />
              </MenuItem>
              {filterOption?.options?.map(option => (
                <MenuItem key={option?.id} value={option?.id}>
                  <Checkbox checked={additionalFilters[filterOption?.name]?.indexOf(option?.id) > -1} />
                  <ListItemText primary={option?.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      }
      case FILTER_OPTION_TYPES.DATE:
        return (
          <FormControl
            key={`filter-option-key-${filterOption.fromDateName}`}
            sx={{ width: filterOption.width }}
            data-testid={`${filterOption.name}-date`}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              {/* TODO: see if we can migrate to generic date picker or date picker v2 */}
              <DatePicker
                label={filterOption.label}
                value={additionalFilters[filterOption.fromDateName] || null}
                minDate={filterOption.minDate}
                onChange={selectedDate => {
                  if (filterOption.onChange) {
                    filterOption.onChange(selectedDate)
                  }
                  if (dayjs(selectedDate).isValid()) {
                    setAdditionalFilters(prevAdditionalFilters => {
                      const newAdditionalFilters = { ...prevAdditionalFilters }
                      if (filterOption.fromDateName) {
                        newAdditionalFilters[filterOption.fromDateName] = getISOLocaleMidnightTime(selectedDate)
                      }
                      if (filterOption.toDateName) {
                        newAdditionalFilters[filterOption.toDateName] = getISOLocaleMidnightTime(
                          addToDate(selectedDate, 1),
                        )
                      }
                      return newAdditionalFilters
                    })
                  }
                }}
                inputFormat={muiDateFormat}
                renderInput={params => {
                  const dateValue = dayjs(params.inputProps.value, dateFormat).valueOf()
                  const inputValue =
                    !isNaN(dateValue) && dayjs(dateValue).isValid() ? dateFormatter(dateValue) : params.inputProps.value
                  return (
                    <TextField
                      variant="outlined"
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        value: inputValue,
                        placeholder: dateFormat,
                      }}
                    />
                  )
                }}
              />
            </LocalizationProvider>
          </FormControl>
        )
      default:
        return null
    }
  }

  const showClearFiltersButton = useMemo(() => {
    const isFilterChanged = additionalFilterOptions.some(filter => {
      const additionalValue = additionalFilters[filter.name]
      const defaultValue = defaultFilter[filter.name]

      // check for null+undefined
      if (
        (additionalValue === null || additionalValue === undefined) &&
        (defaultValue === null || defaultValue === undefined)
      ) {
        return false
      }

      // check arrays match
      if (Array.isArray(additionalValue) && Array.isArray(defaultValue)) {
        return additionalValue.sort().join(',') !== defaultValue.sort().join(',')
      }

      if (additionalValue !== defaultValue) return true
    })

    return !hideClearButton && additionalFilterOptions?.length > 0 && (isFilterChanged || !!textSearchValue)
  }, [textSearchValue, additionalFilters])

  return {
    filteredRows: useMemo(() => {
      if (useBackendSortAndFilter) return filteredRows
      return filteredRows.filter(row =>
        Object.entries(additionalFilters).every(([key, value]) => !value || !row[key] || row[key] === value),
      )
    }, [filteredRows, additionalFilters, useBackendSortAndFilter]),
    additionalFilters,
    renderSearchBar: () => (
      <Stack direction="row" flexWrap="wrap" gap={2}>
        <TextField
          id="outlined-start-adornment"
          placeholder={textSearchPlaceholder}
          sx={{ minWidth: rem(100), maxWidth: rem(300) }}
          value={textSearchValue}
          onChange={handleSearchUpdate}
          size={searchBarSize}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          data-testid="search-grid-input"
        />
        {renderViewModeToggle()}
        {renderSelectFilter()}
        {renderDateRangePicker({ name: 'dateRange', control, watch, maxDate: new Date() })}
        {additionalFilterOptions.map(renderFilterOption)}
        {showClearFiltersButton && (
          <Button
            id="clear-filters-button"
            size="small"
            variant="text"
            onClick={() => {
              additionalFilterOptions.forEach(option => option.onChange && option.onChange())
              setAdditionalFilters(constructResetAdditionalFilters(additionalFilterOptions, defaultFilter))
              setTextSearchValue('')
              filterRows('', rows, true)
              reset()
            }}
          >
            Reset Filters
          </Button>
        )}
        {showFilterChips ? filterChips : null}
      </Stack>
    ),
  }
}
